import React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { t } from 'i18next'
import generalErrorPage from '@app/assets/img/404errorPage.svg'

// no lazy loading for auth pages to avoid flickering
const AuthLayout = React.lazy(() => import('@app/layouts/auth/layout'))
import LoginPage from '@app/pages/auth/login-page'
import MainLayout from '@app/layouts/main/layout'
import RequireAuth from '@app/components/router/require-auth'
import { withLoading } from '@app/hocs/with-loading.hoc'
import DashboardPage from '@/pages/dashboard/dashboard-page'
import ProtectedRoute from './protected-route'
import { useLanguage } from '@/hooks'
import { useAppSelector } from '@app/hooks/reduxHooks'
import { GlobalError } from '@/features/global-error'
import DeviceLayout from '@app/layouts/device/layout'
import SeparateMapLayout from '@app/layouts/separateMap/layout'

const DevicesPage = React.lazy(() => import('@app/pages/admin/devices'))
const FirmsPage = React.lazy(() => import('@app/pages/admin/firms'))
const UsersPage = React.lazy(() => import('@app/pages/admin/users'))
// const PartsPage = React.lazy(() => import('@app/pages/admin/parts-page'))
const InvertersAndAlarmsPage = React.lazy(() => import('@app/pages/admin/inverters-and-alarms'))
const NotificationsPage = React.lazy(() => import('@app/pages/admin/notifications'))
const DevicePage = React.lazy(() => import('@app/pages/admin/device'))
const DeviceAlarmsPage = React.lazy(() => import('@app/pages/device-alarms-page'))
const DeviceHelpPage = React.lazy(() => import('@app/pages/device-help-page'))
// const DeviceUsageMonitoringPage = React.lazy(() => import('@app/pages/device-usage-monitoring-page'))
const TechnicalInspectionPage = React.lazy(() => import('@app/pages/technical-inspection-page'))
const TechnicalInspectionHistoryPage = React.lazy(() => import('@app/pages/technical-inspection-history-page'))
const WizardPage = React.lazy(() => import('@app/pages/admin/wizard'))
const Logout = React.lazy(() => import('./logout'))
const ProfilePage = React.lazy(() => import('@app/pages/profile-page'))
const SeparateMapPage = React.lazy(() => import('@app/pages/separate-map-page'))
const UsersConsentsPage = React.lazy(() => import('@app/pages/rodo/users-consents-page'))
const RodoDocumentsPage = React.lazy(() => import('@app/pages/rodo/rodo-documents-page'))
// const OfferRequestsPage = React.lazy(() => import('@app/pages/admin/offer-requests-page'))

export const DASHBOARD_PATH = '/'

// const OfferRequests = withLoading(OfferRequestsPage)
const Dashboard = withLoading(DashboardPage)
const Devices = withLoading(DevicesPage)
const Firms = withLoading(FirmsPage)
const Users = withLoading(UsersPage)
// const Parts = withLoading(PartsPage)
const Wizard = withLoading(WizardPage)
const InvertersAndAlarms = withLoading(InvertersAndAlarmsPage)
const Notifications = withLoading(NotificationsPage)
const Device = withLoading(DevicePage)
const DeviceAlarms = withLoading(DeviceAlarmsPage)
const DeviceHelp = withLoading(DeviceHelpPage)
// const DeviceUsageMonitoring = withLoading(DeviceUsageMonitoringPage)
const TechnicalInspection = withLoading(TechnicalInspectionPage)
const TechnicalInspectionHistory = withLoading(TechnicalInspectionHistoryPage)
const Profile = withLoading(ProfilePage)
const UsersConsents = withLoading(UsersConsentsPage)
const RodoDocuments = withLoading(RodoDocumentsPage)
const AuthLayoutFallback = withLoading(AuthLayout)
const LogoutFallback = withLoading(Logout)
const SeparateMap = withLoading(SeparateMapPage)

export const AppRouter = () => {
  const { language, setLanguage } = useLanguage()

  if (!language) setLanguage('pl')

  const protectedLayout = (
    <RequireAuth>
      <MainLayout />
    </RequireAuth>
  )

  const HomeRedirect = () => {
    const profile = useAppSelector((s) => s.profile?.data)
    const roles: string[] = profile?.role || ['USER']

    if (roles.includes('ADMIN_RODO')) return <Navigate to={'/rodo/users-consents'} replace />

    return <Dashboard />
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path='/separated-map'
          element={
            <RequireAuth>
              <SeparateMapLayout>
                <SeparateMap />
              </SeparateMapLayout>
            </RequireAuth>
          }
        />
        <Route path={DASHBOARD_PATH} element={protectedLayout}>
          <Route index element={<HomeRedirect />} />
          <Route path='/dashboard' element={<Dashboard />} />
          <Route path='/device/:id' element={<DeviceLayout />}>
            <Route index element={<Device />} />
            <Route path='alarms' element={<DeviceAlarms />} />
            <Route path='technical-inspection' element={<TechnicalInspection />} />
            <Route path='technical-inspection-history' element={<TechnicalInspectionHistory />} />
            <Route path='help' element={<DeviceHelp />} />
            {/* Feature temporarily disabled - Will be enable and implemented at version 2.0 */}
            {/* <Route path='usage-monitoring' element={<DeviceUsageMonitoring />} /> */}
          </Route>
          <Route path='/profile' element={<Profile />} />
          <Route element={<ProtectedRoute requiredRoles={['ADMIN']} />}>
            {/* Feature temporarily disabled - Will be enable and implemented at version 2.0 */}
            {/* <Route path='admin/offer-requests' element={<OfferRequests />} /> */}
            <Route path='admin/users' element={<Users />} />
            <Route path='admin/firms' element={<Firms />} />
            <Route path='admin/devices' element={<Devices />} />
            {/* Feature temporarily disabled - Will be enable and implemented at version 2.0 */}
            {/* <Route path='admin/parts' element={<Parts />} /> */}
            <Route path='admin/inverters-and-alarms' element={<InvertersAndAlarms />} />
            <Route path='admin/notifications' element={<Notifications />} />
            <Route path='admin/wizard' element={<Wizard />} />
          </Route>
          <Route element={<ProtectedRoute requiredRoles={['ADMIN_RODO']} />}>
            <Route path='rodo/users-consents' element={<UsersConsents />} />
            <Route path='rodo/documents' element={<RodoDocuments />} />
          </Route>
        </Route>
        <Route path='/auth' element={<AuthLayoutFallback />}>
          <Route path='login' element={<LoginPage />} />
        </Route>
        <Route path='/logout' element={<LogoutFallback />} />
        <Route
          path='*'
          element={
            <GlobalError
              title={t('error.pageNotFound')}
              description={t('error.pageNotFoundDescription')}
              imgData={{ src: generalErrorPage, alt: 'Error page' }}
            />
          }
        />
      </Routes>
    </BrowserRouter>
  )
}
